/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES, type PropertyOverviewSharedStyles } from './propertyOverviewStyles';

/** Alternate job row — matches PropertyOverview.html `.po-tl-r.is-alt`. */
const JOBS_ALTERNATE_ROW = '#FBF9F3';
/** Row divider — matches PropertyOverview.html `.po-tl-r > *` border. */
const JOBS_ROW_BORDER = '#EDE9DE';

export type PropertyJobsSectionStyles = {
  shared: PropertyOverviewSharedStyles;
  module: SerializedStyles;
  moduleHeader: SerializedStyles;
  moduleTitle: SerializedStyles;
  tableWrap: SerializedStyles;
  table: SerializedStyles;
  headerCell: SerializedStyles;
  sortButton: SerializedStyles;
  row: SerializedStyles;
  alternateRow: SerializedStyles;
  cell: SerializedStyles;
  dateCell: SerializedStyles;
  dateDay: SerializedStyles;
  dateDaySoft: SerializedStyles;
  dateYear: SerializedStyles;
  softDate: SerializedStyles;
  jobName: SerializedStyles;
  secondaryText: SerializedStyles;
  description: SerializedStyles;
  /** The empty 18px track after Updated - the Activity's rail - so Created lines up with Activity's Who. */
  spacerCell: SerializedStyles;
  moneyCell: SerializedStyles;
  totalCell: SerializedStyles;
  changesCell: SerializedStyles;
  footerRow: SerializedStyles;
  footerPad: SerializedStyles;
  footerLabel: SerializedStyles;
  footerTotal: SerializedStyles;
  actionsCell: SerializedStyles;
  empty: SerializedStyles;
};

export const DEFAULT_PROPERTY_JOBS_SECTION_STYLES: PropertyJobsSectionStyles = {
  shared: DEFAULT_PROPERTY_OVERVIEW_SHARED_STYLES,
  /* PropertyOverview.html #poJobsSec · .po-mod.po-act */
  module: css`
    label: PROPERTY_JOBS_MODULE;
    padding: 14px 18px 16px;
  `,
  moduleHeader: css`
    label: PROPERTY_JOBS_MODULE_HEADER;
    margin-bottom: 8px;
  `,
  /* PropertyOverview.html #poJobsSec · .po-act-h */
  moduleTitle: css`
    label: PROPERTY_JOBS_MODULE_TITLE;
    color: ${COLORS.textTertiary};
  `,
  /* PropertyOverview.html · .po-tl-scroll / .po-tl.po-jobs */
  tableWrap: css`
    label: PROPERTY_JOBS_TABLE_WRAP;
    overflow-x: auto;
  `,
  /*
   * The design's grid (PropertyOverview.html .po-jobs), not the table algorithm: Updated, the rail's
   * width, Created, Job, Type, Status, Description, the three money columns and the kebab. Its
   * first five tracks are the Activity table's, so Job and Type start on the same lines in both.
   * The table's rows and groups are display: contents, so the cells are the grid's items; the
   * markup carries explicit table roles, which a display change would otherwise drop.
   */
  table: css`
    label: PROPERTY_JOBS_TABLE;
    display: grid;
    grid-template-columns: 88px 18px 104px 26% 11% minmax(112px, 0.9fr) minmax(70px, 1fr) auto auto auto 36px;
    width: 100%;
    min-width: 860px;

    & thead,
    & tbody,
    & tfoot,
    & tr {
      display: contents;
    }
  `,
  headerCell: css`
    label: PROPERTY_JOBS_HEADER_CELL;
    padding: 9px 10px 8px;
    border-bottom: 1px solid ${COLORS.stroke};
    background: ${COLORS.sunken};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(8.5)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: ${COLORS.textTertiary};
    white-space: nowrap;
    display: flex;
    align-items: flex-end;
  `,
  sortButton: css`
    label: PROPERTY_JOBS_SORT_BUTTON;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    padding: 0;
    background: none;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: ${COLORS.ink};
    cursor: pointer;
  `,
  row: css`
    label: PROPERTY_JOBS_ROW;
    cursor: pointer;

    &:hover > td {
      background: ${COLORS.selectedWash};
    }

    & + & > td {
      border-top: 1px solid ${JOBS_ROW_BORDER};
    }
  `,
  alternateRow: css`
    label: PROPERTY_JOBS_ROW_ALTERNATE;
    & > td {
      background: ${JOBS_ALTERNATE_ROW};
    }
  `,
  cell: css`
    label: PROPERTY_JOBS_CELL;
    padding: 11px 10px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12.5)};
    line-height: 1.4;
    color: ${COLORS.ink};
    display: flex;
    align-items: center;
    min-width: 0;
  `,
  /* PropertyOverview.html · .po-jobs .po-tl-when */
  dateCell: css`
    label: PROPERTY_JOBS_DATE_CELL;
    white-space: nowrap;
    justify-content: flex-start;
    padding-left: 10px;
    padding-right: 10px;
  `,
  dateDay: css`
    label: PROPERTY_JOBS_DATE_DAY;
    display: block;
    font-weight: ${FONT_WEIGHT.bold};
    font-variant-numeric: tabular-nums;
  `,
  dateDaySoft: css`
    label: PROPERTY_JOBS_DATE_DAY_SOFT;
    display: block;
    font-weight: ${FONT_WEIGHT.medium};
    font-variant-numeric: tabular-nums;
    color: ${COLORS.textSecondary};
  `,
  dateYear: css`
    label: PROPERTY_JOBS_DATE_YEAR;
    display: block;
    font-size: ${pxToRem(10.5)};
    font-style: normal;
    color: ${COLORS.textTertiary};
    font-variant-numeric: tabular-nums;
  `,
  softDate: css`
    label: PROPERTY_JOBS_DATE_SOFT;
  `,
  jobName: css`
    label: PROPERTY_JOBS_JOB_NAME;
    border: 0;
    padding: 0;
    background: none;
    font: inherit;
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.ink};
    text-align: left;
    cursor: pointer;
    /* PropertyOverview.html .po-tl-job-n: a long name ends in an ellipsis inside its track. */
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  `,
  secondaryText: css`
    label: PROPERTY_JOBS_SECONDARY_TEXT;
    color: ${COLORS.textSecondary};
  `,
  /* One line, ending in an ellipsis wherever its track ends; the full text is the title. */
  description: css`
    label: PROPERTY_JOBS_DESCRIPTION;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${COLORS.textSecondary};
  `,
  spacerCell: css`
    label: PROPERTY_JOBS_SPACER_CELL;
    padding: 0;
  `,
  /* PropertyOverview.html · .po-tl-money / .po-tl-h.is-num-l */
  moneyCell: css`
    label: PROPERTY_JOBS_MONEY_CELL;
    justify-content: flex-end;
    text-align: right;
    padding-left: 18px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,
  /* PropertyOverview.html · .po-tl-total */
  totalCell: css`
    label: PROPERTY_JOBS_TOTAL_CELL;
    justify-content: flex-end;
    text-align: right;
    padding-left: 18px;
    font-weight: ${FONT_WEIGHT.medium};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,
  /* PropertyOverview.html · .po-chg */
  changesCell: css`
    label: PROPERTY_JOBS_CHANGES_CELL;
    justify-content: flex-end;
    text-align: right;
    padding-left: 18px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    font-weight: ${FONT_WEIGHT.medium};
    color: ${COLORS.textSecondary};
  `,
  footerRow: css`
    label: PROPERTY_JOBS_FOOTER_ROW;

    & > td {
      border-top: 1px solid ${COLORS.stroke};
      background: ${COLORS.sunken};
    }
  `,
  /* PropertyOverview.html .po-jobs-foot-pad: through Initial scope, so Lifetime sits under Changes. */
  footerPad: css`
    label: PROPERTY_JOBS_FOOTER_PAD;
    grid-column: 1 / 9;
    padding: 0;
  `,
  /* PropertyOverview.html · .po-jobs-foot-k — sits under Changes, not Description */
  footerLabel: css`
    label: PROPERTY_JOBS_FOOTER_LABEL;
    justify-content: flex-end;
    text-align: right;
    padding-left: 18px;
    font-weight: ${FONT_WEIGHT.medium};
    color: ${COLORS.ink};
  `,
  footerTotal: css`
    label: PROPERTY_JOBS_FOOTER_TOTAL;
    justify-content: flex-end;
    text-align: right;
    padding-left: 18px;
    font-weight: ${FONT_WEIGHT.bold};
    font-variant-numeric: tabular-nums;
  `,
  /* The row's kebab (RowKebabMenu) positions itself against this cell. */
  actionsCell: css`
    label: PROPERTY_JOBS_ACTIONS_CELL;
    position: relative;
    justify-content: flex-end;
    padding: 0;
  `,
  empty: css`
    label: PROPERTY_JOBS_EMPTY;
    grid-column: 1 / -1;
    padding: 18px 10px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13)};
    color: ${COLORS.textTertiary};
  `,
};
